import type { ArticleMeta, PageElement } from "../state/types";
import { cleanExtractedText, normalizeUrl, textToPage } from "./text";

/**
 * Cloudflare Worker that proxies article fetches.
 *
 * The browser cannot fetch arbitrary origins directly (CORS), so requests go
 * through a worker that also presents a crawler user agent to get the full
 * article body. Override with `VITE_ARTICLE_PROXY` to point at your own
 * deployment; see `worker/README.md`.
 */
export const ARTICLE_PROXY =
  import.meta.env?.VITE_ARTICLE_PROXY ?? "https://erasure-proxy.justin-wolfe.workers.dev";

const proxied = (url: string): string =>
  `${ARTICLE_PROXY}/?url=${encodeURIComponent(url)}`;

/**
 * The extractor and its markdown toolchain are ~400 kB of the bundle and are only
 * needed once a reader actually fetches a URL, so they load on demand. The splash
 * screen, and erasures opened from storage, never pay for them.
 */
const loadParsers = async () => {
  const [{ extractFromHtml }, { default: TurndownService }, { default: removeMarkdown }] =
    await Promise.all([
      import("@extractus/article-extractor"),
      import("turndown"),
      import("remove-markdown"),
    ]);
  return { extractFromHtml, turndown: new TurndownService(), removeMarkdown };
};

export interface ExtractedArticle {
  meta: ArticleMeta;
  page: PageElement[];
}

/** Thrown when the article cannot be fetched or contains no usable text. */
export class ArticleError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "ArticleError";
  }
}

const OFFLINE_MESSAGE =
  "You're offline. Please connect to the internet to fetch new articles.";

const htmlToPlainText = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = html;
  return container.textContent ?? "";
};

/**
 * Fetches an article and converts it into an erasable page.
 *
 * If the extractor cannot identify an article body but the fetch succeeded, falls
 * back to the raw text of the page, which is usually still worth erasing.
 */
export const fetchArticle = async (rawUrl: string): Promise<ExtractedArticle> => {
  const url = normalizeUrl(rawUrl);

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new ArticleError(OFFLINE_MESSAGE);
  }

  let html: string;
  try {
    const response = await fetch(proxied(url));
    if (!response.ok) {
      throw new ArticleError(
        `Could not fetch that page (${response.status} ${response.statusText}).`,
      );
    }
    html = await response.text();
  } catch (error) {
    if (error instanceof ArticleError) throw error;
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new ArticleError(OFFLINE_MESSAGE, error);
    }
    throw new ArticleError(
      "Could not reach that page. Check the URL and try again.",
      error,
    );
  }

  const { extractFromHtml, turndown, removeMarkdown } = await loadParsers();

  let parsed: Awaited<ReturnType<typeof extractFromHtml>> = null;
  try {
    // `extractFromHtml`, not `extract`: the latter treats its second argument as
    // parser options, so passing the URL there silently dropped it.
    parsed = await extractFromHtml(html, url);
  } catch {
    // Extraction failing is expected on unusual pages; fall through to raw text.
    parsed = null;
  }

  const body = parsed?.content
    ? cleanExtractedText(removeMarkdown(turndown.turndown(parsed.content)))
    : cleanExtractedText(htmlToPlainText(html));

  if (body.trim().length === 0) {
    throw new ArticleError("That page didn't contain any text to erase.");
  }

  const title = parsed?.title?.trim();
  const text = title ? `${title}\n\n${body}` : body;

  return {
    meta: {
      title: title || undefined,
      url,
      author: parsed?.author || undefined,
      published: parsed?.published || undefined,
      description: parsed?.description || undefined,
      image: parsed?.image || undefined,
    },
    page: textToPage(text),
  };
};

/** Builds a page directly from text the reader pasted in. */
export const articleFromText = (text: string): ExtractedArticle => {
  const cleaned = cleanExtractedText(text);
  if (cleaned.length === 0) {
    throw new ArticleError("Paste some text or a link to get started.");
  }
  const [firstLine] = cleaned.split("\n");
  return {
    meta: {
      title: firstLine.length > 60 ? `${firstLine.slice(0, 57)}…` : firstLine,
    },
    page: textToPage(cleaned),
  };
};
